import React, {useContext, useEffect, useState} from 'react';
import {StyleSheet, Text, View, TouchableOpacity} from 'react-native';
import {GlobalStateContext} from '../general/GlobalStateProvider';
import DataOps from '../../data/DataOps';
import CampsListView from '../../screens/explore_screen/components/views/CampsListView';

const Sessions = () => {
    const {user: user, setUser: setUser, padding: padding} = useContext(GlobalStateContext);

    const [activeTab, setActiveTab] = useState(() => {
        try {
            const stored = localStorage.getItem('sessionsPageActiveTab');
            return stored ? stored : 'upcoming';
        } catch {
            return 'upcoming';
        }
    });
    const [upcomingSessions, setUpcomingSessions] = useState([]);
    const [ongoingSessions, setOngoingSessions] = useState([]);
    const [pastSessions, setPastSessions] = useState([]);

    useEffect(() => {
        const fetchUserSessions = async () => {
            try {
                if (!user?.userId) {
                    return;
                }

                const sessionsUpcoming: any[] = [];
                const sessionsOngoing: any[] = [];
                const sessionsPast: any[] = [];

                const classifySession = (sessionData: any) => {
                    if (!sessionData || (sessionData as any).isHidden === true || (sessionData as any).deleted === true) return;

                    if (!sessionData.startAt || !sessionData.endAt) {
                        return
                    }

                    const now = new Date();
                    const start = (sessionData as any).startAt.toDate();
                    const end = (sessionData as any).endAt.toDate();

                    if (end < now) {
                        sessionsPast.push(sessionData);
                    } else if (start > now) {
                        sessionsUpcoming.push(sessionData);
                    } else {
                        sessionsOngoing.push(sessionData);
                    }
                };

                if (user.accountType === 'host') {
                    // Host: aggregate sessions from all owned camps
                    const campIds: string[] = Array.isArray((user as any).camps) ? (user as any).camps : [];
                    for (const campId of campIds) {
                        try {
                            const sessionsMap = await DataOps.fetchSessionsForCamp(campId);
                            const sessionList = Object.values(sessionsMap || {});
                            for (const sessionData of sessionList) classifySession(sessionData);
                        } catch {}
                    }
                } else if (user.accountType === 'coach') {
                    // Coach: sessions where this coach is assigned (past/ongoing/upcoming)
                    try {
                        const sessionList = await DataOps.fetchSessionsByCoach(user.userId);
                        for (const sessionData of sessionList || []) classifySession(sessionData);
                    } catch {}
                } else {
                    // Family/other: derive sessions from registration table by userId
                    const regs = await DataOps.fetchActiveRegistrationsByUser(user.userId);
                    const sessionIds = Array.from(new Set((regs || []).map((r: any) => r.sessionId).filter(Boolean)));
                    for (const sessionId of sessionIds) {
                        try {
                            const sessionData = await DataOps.fetchDataFromFirebase('session', sessionId);
                            classifySession(sessionData);
                        } catch (sessionError) {
                            console.error('Error fetching session:', sessionId, sessionError);
                        }
                    }
                }

                // Sort sessions by start date
                const sortByStartDate = (a, b) => {
                    const dateA = a.startAt.toDate();
                    const dateB = b.startAt.toDate();
                    return dateA.getTime() - dateB.getTime();
                };

                sessionsUpcoming.sort(sortByStartDate);
                sessionsOngoing.sort(sortByStartDate);
                sessionsPast.sort((a, b) => -sortByStartDate(a, b));

                setUpcomingSessions(sessionsUpcoming);
                setOngoingSessions(sessionsOngoing);
                setPastSessions(sessionsPast);

                // Smart default tab selection - prioritize ongoing sessions
                if (activeTab === 'upcoming' && sessionsOngoing.length > 0 && sessionsUpcoming.length === 0) {
                    setActiveTab('ongoing');
                }
            } catch (error) {
                console.error('Error fetching user sessions:', error);
            }
        };

        fetchUserSessions();
    }, [user?.userId]);

    useEffect(() => {
        try { localStorage.setItem('sessionsPageActiveTab', activeTab); } catch {}
    }, [activeTab]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'upcoming':
                return upcomingSessions.length === 0 ? (
                    <View style={styles.emptyContainer}>
                        <Text style={styles.descriptionText}>No upcoming SnapCamp sessions.</Text>
                        <Text style={styles.emptySubtext}>Browse camps to find and register for new sessions!</Text>
                    </View>
                ) : (
                    <CampsListView camps={[]} sessions={upcomingSessions} isSessionView={true} sessionsHistory={true}/>
                );
            case 'ongoing':
                return ongoingSessions.length === 0 ? (
                    <View style={styles.emptyContainer}>
                        <Text style={styles.descriptionText}>No ongoing SnapCamp sessions.</Text>
                        <Text style={styles.emptySubtext}>Your active sessions will appear here when they start.</Text>
                    </View>
                ) : (
                    <CampsListView camps={[]} sessions={ongoingSessions} isSessionView={true} sessionsHistory={true}/>
                );
            case 'past':
                return pastSessions.length === 0 ? (
                    <View style={styles.emptyContainer}>
                        <Text style={styles.descriptionText}>No past SnapCamp sessions.</Text>
                        <Text style={styles.emptySubtext}>Sessions you've completed will appear here.</Text>
                    </View>
                ) : (
                    <CampsListView camps={[]} sessions={pastSessions} isSessionView={true} sessionsHistory={true}/>
                );
            default:
                return null;
        }
    };

    return (
        <View style={[styles.container, {paddingHorizontal: padding}]}>
            <View style={styles.tabNavigation}>
                <TouchableOpacity
                    style={[styles.tabButton, activeTab === 'upcoming' && styles.activeTabButton]}
                    onPress={() => setActiveTab('upcoming')}
                >
                    <Text style={[styles.tabText, activeTab === 'upcoming' && styles.activeTabText, activeTab === 'upcoming' && styles.upcomingText]}>
                        Upcoming Sessions
                    </Text>
                    {upcomingSessions.length > 0 && (
                        <View style={[styles.countBadge, styles.upcomingBadge]}>
                            <Text style={styles.countText}>{upcomingSessions.length}</Text>
                        </View>
                    )}
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.tabButton, activeTab === 'ongoing' && styles.activeTabButton]}
                    onPress={() => setActiveTab('ongoing')}
                >
                    <Text style={[styles.tabText, activeTab === 'ongoing' && styles.activeTabText, activeTab === 'ongoing' && styles.ongoingText]}>
                        Ongoing Sessions
                    </Text>
                    {ongoingSessions.length > 0 && (
                        <View style={[styles.countBadge, styles.ongoingBadge]}>
                            <Text style={styles.countText}>{ongoingSessions.length}</Text>
                        </View>
                    )}
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.tabButton, activeTab === 'past' && styles.activeTabButton]}
                    onPress={() => setActiveTab('past')}
                >
                    <Text style={[styles.tabText, activeTab === 'past' && styles.activeTabText, activeTab === 'past' && styles.pastText]}>
                        Past Sessions
                    </Text>
                    {pastSessions.length > 0 && (
                        <View style={[styles.countBadge, styles.pastBadge]}>
                            <Text style={styles.countText}>{pastSessions.length}</Text>
                        </View>
                    )}
                </TouchableOpacity>
            </View>

            {/* Tab Content */}
            <View style={styles.tabContent}>
                {renderTabContent()}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    description: {
        alignItems: 'center'
    },
    descriptionText: {
        fontSize: 18,
        fontWeight: "600",
        color: '#374151',
        textAlign: 'center',
    },
    emptySubtext: {
        fontSize: 14,
        fontWeight: "400",
        color: '#9CA3AF',
        marginTop: 8,
        textAlign: 'center',
        lineHeight: 20,
    },
    titleText: {
        fontSize: 22,
        fontWeight: 'bold',
        marginBottom: 40,
    },
    container: {
        flex: 1,
        paddingTop: 24,
        backgroundColor: '#FFFFFF',
    },
    tabsContainer: {
        flexDirection: 'column',
        marginBottom: 10,
        paddingHorizontal: 50,
        paddingTop: 20,
    },
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: 60,
        paddingHorizontal: 32,
    },
    tabNavigation: {
        flexDirection: 'row',
        marginBottom: 24,
        marginHorizontal: 16,
        backgroundColor: '#F8F9FA',
        borderRadius: 16,
        padding: 4,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    tabButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 14,
        paddingHorizontal: 12,
        borderRadius: 12,
        backgroundColor: 'transparent',
        minHeight: 48,
    },
    activeTabButton: {
        backgroundColor: '#FFFFFF',
        shadowColor: '#2C67D6',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
        elevation: 3,
    },
    tabText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#6B7280',
        textAlign: 'center',
        flex: 1,
    },
    activeTabText: {
        fontWeight: '700',
    },
    upcomingText: {
        color: '#2C67D6', // Blue for upcoming
    },
    ongoingText: {
        color: '#10B981', // Green for ongoing
    },
    pastText: {
        color: '#6B7280', // Gray for past
    },
    countBadge: {
        borderRadius: 10,
        minWidth: 20,
        height: 20,
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 6,
    },
    upcomingBadge: {
        backgroundColor: '#2C67D6', // Blue for upcoming
    },
    ongoingBadge: {
        backgroundColor: '#10B981', // Green for ongoing
    },
    pastBadge: {
        backgroundColor: '#6B7280', // Gray for past
    },
    countText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '700',
        lineHeight: 14,
    },
    tabContent: {
        flex: 1,
        paddingHorizontal: 16,
    },
});

export default Sessions;